import MicroModal from 'micromodal';
import {
    pauseYoutubeIframe,
    playYoutubeIframe,
    releaseYoutubeIframe,
} from '@core/directives/youtube-player.js';
import commonUtils from '@utils/common.ts';

/**
 * ModalController
 * Internal controller used by the `v-modal` Vue 3 directive.
 *
 * - Attaches click + keypress listeners to the bound element to open a MicroModal instance.
 * - Prevents modal stacking by closing any currently open modal before opening a new one
 *   (required for gallery arrows and playlist navigation).
 * - Modal DOM is teleported outside nested stacking contexts by AModal.
 * - Handles media lifecycle:
 *     • autoplay/pause for video and audio players while preserving playback position
 *     • readiness-safe YouTube iframe play/pause control
 * - Emits analytics events (open/close/video interactions) via dataLayer.
 * - Reads modal event metadata from localStorage (set by media components).
 * - Emits Vue-compatible events using vnode.props handlers (works with both
 *   Composition API and Options API consumers).
 */

class ModalController {
    constructor(el, id, vnode) {
        this.el = el;
        this.id = id;
        this.vnode = vnode;

        this.events = {};
        this.startTime = null;

        this.isOpening = false;
        this.openLockTimer = null;

        this.openModal = this.openModal.bind(this);
        this.closeModal = this.closeModal.bind(this);

        if (this.el && this.id) {
            this.init();
        }
    }

    init() {
        requestAnimationFrame(() => {
            this.el.addEventListener('click', this.openModal);
            this.el.addEventListener('keypress', this.openModal);
        });
    }

    update(id, vnode) {
        this.id = id;
        this.vnode = vnode;
    }

    getCurrentOpenModalId() {
        const openEl = document.querySelector('.a-modal.is-modal-open');
        return openEl?.id || null;
    }

    closeCurrentlyOpenModal() {
        const openId = this.getCurrentOpenModalId();
        if (openId && openId !== this.id) {
            try {
                MicroModal.close(openId);
            } catch {
                // avoid throw
            }
        }
    }

    openModal(e) {
        if (this.isOpening) return;

        // MicroModal (debugMode off) does not validate the target id. Calling show()
        // for a missing element leaves its internal activeModal in a corrupt state
        // (modal === null), which then throws on the next show() at removeEventListeners.
        // Bail out early if the modal element is not in the DOM.
        if (!document.getElementById(this.id)) return;

        this.isOpening = true;
        clearTimeout(this.openLockTimer);
        this.openLockTimer = setTimeout(() => {
            this.isOpening = false;
            this.openLockTimer = null;
        }, 200);

        e.preventDefault();
        this.closeCurrentlyOpenModal();

        MicroModal.show(this.id, {
            openClass: 'is-modal-open',
            disableFocus: true,

            onShow: (modal) => {
                document.documentElement.classList.add('is-modal-open');
                this.keyHandler = (event) => {
                    if (!modal.querySelector('.a-modal-gallery')) return;

                    if (event.key === 'ArrowLeft') {
                        const prevBtn = modal.querySelector('.a-modal-gallery .prev');
                        prevBtn?.click();
                    }

                    if (event.key === 'ArrowRight') {
                        const nextBtn = modal.querySelector('.a-modal-gallery .next');
                        nextBtn?.click();
                    }
                };
                document.addEventListener('keydown', this.keyHandler);

                const closeButtons = modal?.querySelectorAll?.('[data-close]') || [];
                const youtube = modal?.querySelector?.('.a-youtube');
                const video = modal?.querySelector?.('.a-modal__video-player');
                const audio = modal?.querySelector?.('.a-modal__audio-player');

                this.startTime = new Date();

                try {
                    this.events = JSON.parse(localStorage.getItem('modalEvents'));
                } catch {
                    this.events = { open: null, close: null };
                }

                localStorage.removeItem('modalEvents');

                if (this.events?.open) {
                    this.sendEvent({
                        event: 'Acronis',
                        eventCategory: 'Interactions',
                        eventAction: 'Start product tour',
                        ...this.events.open,
                    });
                }

                if (closeButtons.length > 0) {
                    closeButtons.forEach((element) => element.addEventListener('click', this.closeModal));
                }

                video?.play()?.catch?.(() => {});
                audio?.play()?.catch?.(() => {});

                if (youtube) {
                    this.youtube = youtube;
                    const ytBaseUrl = (youtube.dataset?.src || '').split('?')[0];

                    this.sendEvent({
                        event: 'Acronis',
                        eventAction: 'Video',
                        eventContext: 'Play',
                        eventCategory: 'Interactions',
                        eventLabel: youtube.dataset?.title || ytBaseUrl,
                    });

                    playYoutubeIframe(youtube);

                    if (window.OptanonActiveGroups && window.ytTracker) {
                        const consentGiven = window.OptanonActiveGroups?.includes('C0002');
                        if (consentGiven) {
                            window.ytTracker?.init();
                        }
                    }
                }

                /* If you don't use timer here, it will fire onClose before the onShow when using gallery/playlist modals
                    * Causing it to enable the scroll again, both with commonUtils and witht he native disableScroll function
                    */
                setTimeout(() => {
                    commonUtils.globalOverflowProperty('hidden');
                });
            },

            onClose: (modal) => {
                document.documentElement.classList.remove('is-modal-open');
                document.removeEventListener('keydown', this.keyHandler);
                const endTime = new Date();
                const timeOnDemoText = (s) => {
                    if (s < 10) return '0–10sec';
                    if (s < 30) return '11–30sec';
                    if (s < 60) return '31–60sec';
                    if (s < 180) return '1-3mins';
                    if (s < 300) return '3-5mins';
                    if (s < 600) return '5-10mins';
                    if (s < 900) return '10-15mins';
                    if (s < 1200) return '15-20mins';
                    if (s < 1800) return '20-30mins';
                    return '30+ minutes';
                };

                if (this.events?.close) {
                    this.sendEvent({
                        event: 'Acronis',
                        eventCategory: 'Interactions',
                        eventAction: 'Start product tour',
                        eventContext: timeOnDemoText((endTime - this.startTime) / 1000),
                        ...this.events.close,
                    });
                }

                const closeButtons = modal?.querySelectorAll?.('[data-close]') || [];
                const youtube = modal?.querySelector?.('.a-youtube');
                const video = modal?.querySelector?.('.a-modal__video-player');
                const audio = modal?.querySelector?.('.a-modal__audio-player');

                if (closeButtons.length > 0) {
                    closeButtons.forEach((element) => element.removeEventListener('click', this.closeModal));
                }

                video?.pause();
                audio?.pause();
                pauseYoutubeIframe(youtube);
                this.emit('close', this.el);
                commonUtils.globalOverflowProperty('auto');
            },
        });
    }

    closeModal(e) {
        e.preventDefault();
        e.stopPropagation?.();
        MicroModal.close(this.id);
    }

    sendEvent(event) {
        if (typeof event !== 'object') return;

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(event);
    }

    emit(name, data) {
        const props = this.vnode?.props || {};
        const handler =
            props[`on${name.charAt(0).toUpperCase()}${name.slice(1)}`] ||
            props[`on${name}`] ||
            null;

        if (typeof handler === 'function') {
            handler(data);
        }
    }

    destroy() {
        this.el.removeEventListener('click', this.openModal);
        this.el.removeEventListener('keypress', this.openModal);
        clearTimeout(this.openLockTimer);
        releaseYoutubeIframe(this.youtube);
    }
}

const controllers = new WeakMap();
export default {
    mounted(el, binding, vnode) {
        if (!binding?.value) return;

        const controller = new ModalController(el, binding.value, vnode);
        controllers.set(el, controller);
    },

    updated(el, binding, vnode) {
        if (!binding?.value) return;

        controllers.get(el)?.update(binding.value, vnode);
    },

    unmounted(el) {
        const controller = controllers.get(el);

        if (controller) {
            controller.destroy();
            controllers.delete(el);
        }
    },
};
